import { Box, CircularProgress } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import LogIcon from 'components/atoms/LogIcon'
import PageLoading from 'components/atoms/PageLoading'
import PageTitle from 'components/atoms/PageTitle'
import SessionsSelect from 'components/atoms/SessionsSelect'
import RulesContent from 'components/organisms/Rules/RulesContent'

import useEntityData from 'hooks/useEntityData'
import useLogs from 'hooks/useLogs'
import useQuery from 'hooks/useQuery'
import useSession from 'hooks/useSession'
import useSessionsCurrentSession from 'hooks/useSessionsCurrentSession'

import {
  ROLES_ARVAL,
  ROLE_B2C,
  ROLE_LOGISTICS,
  ROLE_POST_SALES,
  ROLE_READ_ONLY,
  useAuth,
} from '../../auth'

export default function Rules() {
  const { t } = useTranslation()
  const { items } = useEntityData({ entity: 'sessions', newPage: 0 })

  const { currentSession } = useSessionsCurrentSession()
  const { sessionId } = useParams()

  const query = useQuery()
  const listType = query.get('type') || 'grouping'

  const navigate = useNavigate()

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const sessions = useMemo(() => {
    let confirmedSessions = items.filter(
      (session) => session.confirmationAcceptedAt
    )

    if (currentSession?.data) {
      confirmedSessions = [currentSession.data, ...confirmedSessions]
    }

    return confirmedSessions
  }, [currentSession, items])

  const { isFetching: isFetchingSession, fetchSession, session } = useSession()

  useEffect(() => {
    if (
      !currentSession?.data ||
      currentSession.data.sessionId === sessionId ||
      !sessionId
    ) {
      return
    }
    fetchSession(sessionId)
  }, [currentSession.data, fetchSession, sessionId])

  const { hasOneOfRoles } = useAuth()
  const canAccess = useMemo(() => hasOneOfRoles(ROLES_ARVAL), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () =>
      hasOneOfRoles([
        ROLE_READ_ONLY,
        ROLE_LOGISTICS,
        ROLE_B2C,
        ROLE_POST_SALES,
      ]),
    [hasOneOfRoles]
  )

  if (!canAccess) {
    return <Navigate to="/agenda" />
  }

  if (currentSession.fetching || !currentSession?.data || isFetchingSession) {
    return <PageLoading />
  }

  if (!sessionId) {
    return <Navigate to={`/rules/${currentSession?.data?.sessionId}`} />
  }

  return (
    <div className="content-container" data-testid="main-container">
      <PageTitle
        label={t('rules.title')}
        rightColumn={
          <>
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              label={t('common.form.downloadLog')}
              handleClick={() =>
                saveLogs(`logs_partner_rules_${listType}`, {
                  area: t(`common.logs.partner-rules-${listType}`),
                })
              }
              showLabel
            />
            <Box pr={2} />
            <SessionsSelect
              sessions={sessions}
              value={sessionId}
              handleSessionChange={(e) => {
                navigate(`/rules/${e.target.value}`)
              }}
            />
          </>
        }
      />

      <RulesContent
        disableEditsGroup={
          !currentSession?.data || currentSession?.data?.sessionId !== sessionId
        }
        disableEditsCost={
          !currentSession?.data || currentSession?.data?.sessionId === sessionId
            ? !!currentSession.data.assignmentStartedAt
            : !!session.assignmentStartedAt
        }
        disableEditsParameters={
          !currentSession?.data || currentSession?.data?.sessionId === sessionId
            ? !!currentSession.data.assignmentEndedAt
            : !!session.assignmentEndedAt
        }
        isCurrentSessionSelected={currentSession?.data?.sessionId === sessionId}
        selectedSessionId={sessionId}
        contentIsReadOnly={userRoleHasReadOnly}
      />
    </div>
  )
}
